import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import { type ProductType, type Prompt } from "@1js/pl-card-components";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
} from "@fluentui/react-components";
import { CopyFilled, CopyRegular } from "@fluentui/react-icons";
import * as React from "react";
import { useLoader, type LoaderStateType } from "./Loader.web.hook";
import {
  CancelButtonLabel,
  CopiedButtonLabel,
  CopyButtonLabel,
  CouldNotLoadPromptHeader,
  CouldNotLoadPromptMessage,
  LoadingMessage,
  TryInAppByCopyHeader,
  TryInAppByCopyMessage,
} from "./Loader.web.string";

export type LoaderProps = {
  onCancel: () => void;
  onCopy: () => void;
  destination: ProductType | string;
  state: LoaderStateType;
  prompt: Prompt;
};

const useStyles = makeStyles({
  dialogActions: {
    display: "flex",
    justifyContent: "stretch",
    width: "100%",
  },
  button: {
    alignSelf: "stretch",
    width: "100%",
  },
  dialogSurface: {
    maxWidth: "320px",
  },
  spinner: {
    paddingBottom: "10px",
  },
  centeredContent: {
    textAlign: "center",
  },
  dialogContent: {
    overflowY: "visible",
    height: "fit-content",
  },
});

export const Loader: React.FC<LoaderProps> = ({
  onCancel,
  onCopy,
  destination,
  state,
  prompt,
}) => {
  const {
    onCancel: onCancelHooked,
    onCopy: onCopyHooked,
    isCopied,
    productName,
  } = useLoader({
    onCancel,
    onCopy,
    destination,
    prompt,
  });

  const localizations = useStrings({
    CancelButtonLabel,
    CopyButtonLabel,
    CouldNotLoadPromptHeader,
    CopiedButtonLabel,
  });

  const localizationsWithPlaceholders = useStringsWithPlaceholders({
    CouldNotLoadPromptMessage,
    LoadingMessage,
    TryInAppByCopyHeader,
    TryInAppByCopyMessage,
  });

  const styles = useStyles();

  return (
    <Dialog
      open={true}
      onOpenChange={onCancel}
      modalType={state === "loading" ? "alert" : "modal"}
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          {state === "error" ? (
            <DialogTitle>
              {localizations.CouldNotLoadPromptHeader()}
            </DialogTitle>
          ) : (
            state === "copy" && (
              <DialogTitle>
                {localizationsWithPlaceholders.TryInAppByCopyHeader({
                  productName: destination,
                })}
              </DialogTitle>
            )
          )}
          <DialogContent className={styles.dialogContent}>
            {state === "loading" ? (
              <>
                <Spinner className={styles.spinner} />
                <p className={styles.centeredContent}>
                  {localizationsWithPlaceholders.LoadingMessage({
                    productName,
                  })}
                </p>
              </>
            ) : state === "error" ? (
              <p>
                {localizationsWithPlaceholders.CouldNotLoadPromptMessage({
                  productName,
                })}
              </p>
            ) : (
              state === "copy" && (
                <p>
                  {localizationsWithPlaceholders.TryInAppByCopyMessage({
                    productName: destination,
                  })}
                </p>
              )
            )}
          </DialogContent>
          <DialogActions className={styles.dialogActions} fluid>
            {state === "loading" ? (
              <Button
                appearance="secondary"
                onClick={onCancelHooked}
                className={styles.button}
              >
                {localizations.CancelButtonLabel()}
              </Button>
            ) : (
              (state === "error" || state === "copy") && (
                <Button
                  appearance="primary"
                  onClick={onCopyHooked}
                  className={styles.button}
                  icon={isCopied ? <CopyFilled /> : <CopyRegular />}
                >
                  {isCopied
                    ? localizations.CopiedButtonLabel()
                    : localizations.CopyButtonLabel()}
                </Button>
              )
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
